import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { fetchR2Files, fetchR2Buckets } from 'src/actions/watcher/r2FilesActions';
import { queryParams } from 'src/helpers/UrlHelpers';

import PageLayout from '../_partials/PageLayout';
import PageHeader from 'src/componets/PageHeader';
import Wrapper from 'src/componets/Wrapper';
import NavBarThree from 'src/componets/NavBarThree';
import { FolderAddOutlined, UploadOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

import { R2BucketInterface } from 'src/store/watcher/r2FilesSlice';
import Breadcrumb from './_partials/_Breadcrumb';
import FolderList from './_partials/_FolderList';
import FileList from './_partials/_FileList';
import FileThumbnails from './_partials/_FileThumbnails';
import UploadDrawer from './_partials/_UploadDrawer';
import CreateFolderModal from './_partials/_CreateFolderModal';

const DEFAULT_VIEW = 'list';

const R2Files = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefix = searchParams.get('prefix') || '';
  const bucketId = searchParams.get('bucket_id') || '';
  const listType: any = queryParams.get(searchParams, 'view') || DEFAULT_VIEW;
  const status = useAppSelector(state => state.watcher.r2Files.status);
  const buckets = useAppSelector(state => state.watcher.r2Files.buckets);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchR2Buckets());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchR2Files({ prefix, bucket_id: bucketId || undefined }));
  }, [prefix, bucketId, dispatch]);

  function buildParams(overrides: Record<string, string> = {}) {
    const params: Record<string, string> = {};
    const bid = overrides.bucket_id !== undefined ? overrides.bucket_id : bucketId;
    const pfx = overrides.prefix !== undefined ? overrides.prefix : prefix;
    if (bid) params.bucket_id = bid;
    if (pfx) params.prefix = pfx;
    const view = queryParams.get(searchParams, 'view');
    if (view) params.view = view;
    return params;
  }

  const navigateToFolder = (folderPrefix: string) => {
    setSearchParams(buildParams({ prefix: folderPrefix }));
  };

  function bucketLinkParams(bid: string) {
    const params: Record<string, string> = { bucket_id: bid };
    const view = queryParams.get(searchParams, 'view');
    if (view) params.view = view;
    return '?' + new URLSearchParams(params).toString();
  }

  function onUploadSuccess() {
    dispatch(fetchR2Files({ prefix, bucket_id: bucketId || undefined }));
  }

  function onFolderCreated() {
    setFolderModalOpen(false);
  }

  return (
    <PageLayout className='v-r2files'>
      <PageHeader title='R2 File Manager' />

      <Wrapper style={{ justifyContent: 'space-between' }}>
        <NavBarThree align='left'>
          {buckets.length > 1 && buckets.map((bucket: R2BucketInterface) => (
            <NavBarThree.Link
              key={bucket.id}
              className={bucketId === bucket.id || (!bucketId && bucket === buckets[0]) ? 'active' : ''}
              to={bucketLinkParams(bucket.id)}
            >
              {bucket.label}
            </NavBarThree.Link>
          ))}
        </NavBarThree>

        <NavBarThree align='right'>
          <NavBarThree.Button className='v-link2' onClick={() => setUploadOpen(true)}>
            <UploadOutlined /> Upload Files
          </NavBarThree.Button>
          <NavBarThree.Button className='v-link2' onClick={() => setFolderModalOpen(true)}>
            <FolderAddOutlined /> New Folder
          </NavBarThree.Button>
        </NavBarThree>
      </Wrapper>

      <div className='v-container'>
        <Breadcrumb prefix={prefix} onNavigate={navigateToFolder} />

        <Spin spinning={status === 'loading'}>
          <FolderList onNavigate={navigateToFolder} />
          {listType === 'grid' ? <FileThumbnails /> : <FileList />}
        </Spin>
      </div>

      <UploadDrawer
        open={uploadOpen}
        prefix={prefix}
        bucketId={bucketId}
        onClose={() => setUploadOpen(false)}
        onSuccess={onUploadSuccess}
      />
      <CreateFolderModal
        open={folderModalOpen}
        prefix={prefix}
        onClose={() => setFolderModalOpen(false)}
        onCreated={onFolderCreated}
      />
    </PageLayout>
  );
};

export default R2Files;
